/**
 * Money utilities.
 *
 * Money is represented as INTEGER CENTS everywhere in application logic. Never do
 * arithmetic or comparisons on floating-point dollars — parse to cents at the
 * boundary, compute in cents (exact), and format to a dollar string only for
 * display or API payloads.
 *
 * Rationale: floating-point dollars led to one-cent epsilon hacks in comparisons
 * and silent `parseFloat(x || 0)` fallbacks that masked a real bug (see the RTO
 * "balance is always $0" incident). Integer cents make comparisons exact and let
 * missing/invalid values surface as `null` instead of a deceptive `0`.
 */

/**
 * Parse a dollar amount into integer cents.
 *
 * Accepts strings ("1,234.56", "$12", ".5"), numbers (dollars), null/undefined.
 * Returns `null` for empty or invalid input — callers decide how to treat
 * "missing"; do NOT silently coerce to 0, which hides bugs.
 *
 * Values with more than two decimal places are truncated (inputs are 2dp money).
 *
 * @param {string|number|null|undefined} value
 * @returns {number|null} integer cents, or null when not a valid amount
 */
export function dollarsToCents(value) {
  if (value === null || value === undefined) return null;
  let s = String(value).trim().replace(/[$,\s]/g, '');
  if (s === '') return null;

  let negative = false;
  if (s.startsWith('-')) {
    negative = true;
    s = s.slice(1);
  }

  // Digits with an optional single decimal point. Reject "." and other junk.
  if (s === '' || s === '.' || !/^\d*\.?\d*$/.test(s)) return null;

  const [intPart = '', fracPart = ''] = s.split('.');
  const frac2 = (fracPart + '00').slice(0, 2);
  const cents = Number(intPart || '0') * 100 + Number(frac2 || '0');
  if (!Number.isFinite(cents)) return null;
  return negative ? -cents : cents;
}

/**
 * Format integer cents for display, e.g. 123456 -> "$1,234.56", -1234 -> "-$12.34".
 * @param {number|null|undefined} cents
 * @returns {string}
 */
export function formatCents(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '$0.00';
  const negative = n < 0;
  const abs = Math.abs(Math.trunc(n));
  const dollars = Math.floor(abs / 100).toLocaleString('en-US');
  const frac = String(abs % 100).padStart(2, '0');
  return `${negative ? '-' : ''}$${dollars}.${frac}`;
}

/**
 * Convert integer cents to a plain decimal string for number inputs and API
 * payloads, e.g. 123456 -> "1234.56". No symbol or thousands separators.
 * @param {number|null|undefined} cents
 * @returns {string} decimal string, or '' when cents is not finite
 */
export function centsToAmountString(cents) {
  if (cents === null || cents === undefined) return '';
  const n = Number(cents);
  if (!Number.isFinite(n)) return '';
  const negative = n < 0;
  const abs = Math.abs(Math.trunc(n));
  return `${negative ? '-' : ''}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/**
 * Single source of truth for the client-side "payment exceeds balance" rule.
 * Uses exact integer-cents comparison (no floating-point epsilon).
 *
 * @param {number|null} amountCents - tendered amount in cents
 * @param {number|null|undefined} maxCents - balance cap in cents (null/undefined = no cap)
 * @param {string} label - phrase used in the error, e.g. "remaining balance"
 * @returns {string|null} error message, or null if the amount is valid
 */
export function validatePaymentAgainstBalance(amountCents, maxCents, label = 'balance') {
  if (amountCents === null || amountCents === undefined || amountCents <= 0) {
    return 'Please enter a valid amount';
  }
  if (maxCents !== null && maxCents !== undefined && amountCents > maxCents) {
    return `Amount ${formatCents(amountCents)} exceeds ${label} of ${formatCents(maxCents)}`;
  }
  return null;
}
